import React, { useMemo } from 'react'
import { Grid } from '../../shop-reception/Common/Grid'
import { Alert, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { CircularProgress } from '@mui/material'
import { useQuery } from 'react-query'
import { EligibilityRulesResponse, EligibilityRuleDTO } from '../interfaces'
import { fetchEligibilityRules } from '../api'
import { ruleToDate, splitRulesByEffectiveDate } from "../utils"

const formatRoDate = (d: Date) =>
  d.toLocaleDateString('ro-RO', { year: 'numeric', month: '2-digit', day: '2-digit' })

const RulesBullets: React.FC<{ rule: EligibilityRuleDTO }> = ({ rule }) => {
  return (
    <ul style={{ paddingLeft: '20px', lineHeight: 1.8, marginTop: 8 }}>
      <li>Valoare minimă tranzacție eligibilă (pe zi): {rule.minimumAmountDay} RON</li>
      <li>Valoare maximă tranzacție eligibilă (pe zi): {rule.maximumAmountDay} RON</li>
      <li>Valoare minimă totală lunară eligibilă: {rule.minimumAmountMonth} RON</li>
      <li>Valoare maximă totală lunară eligibilă: {rule.maximumAmountMonth} RON</li>
      <li>Maximum {rule.transactionsPerDay} tranzacții eligibile pe zi</li>
      <li>Procent cashback: {rule.cashbackPercent}%</li>
    </ul>
  )
}

const EligibilityRulesPage: React.FC = () => {
  const queryParams = useMemo(() => ({ page: 0, size: 10 }), [])

  const { data, isLoading, isError } = useQuery<EligibilityRulesResponse>(
    ['employee-discounts-rules', queryParams],
    () => fetchEligibilityRules(queryParams),
    { keepPreviousData: true }
  )

  const { currentRule, history, future } = useMemo(
    () => splitRulesByEffectiveDate(data?.list ?? []),
    [data?.list]
  )

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <CircularProgress color="primary" disableShrink />
      </div>
    )
  }

  if (isError || !data || !currentRule) {
    return (
      <Grid
        maxWidth="sm"
        gridContainerStyle={{ marginTop: '20px' }}
        items={[
          <Typography key="title" variant="medium" component="h2" style={{ textAlign: 'center' }}>
            Discount angajați – Reguli de eligibilitate
          </Typography>,
          <Alert key="error" severity="error">
            A apărut o eroare la încărcarea regulilor. Vă rugăm să reîncărcați pagina.
          </Alert>,
        ]}
        itemsPerRow={1}
      />
    )
  }

  const effectiveFrom = ruleToDate(currentRule)

  return (
    <Grid
      maxWidth="md"
      gridContainerStyle={{ marginTop: '20px' }}
      items={[
        <Typography key="title" variant="medium" component="h2" style={{ textAlign: 'center' }}>
          Discount angajați – Reguli de eligibilitate
        </Typography>,

        <div key="description">
          <p>
            Aceste reguli sunt folosite pentru a determina ce tranzacții sunt eligibile și pentru a
            calcula cashback-ul lunar.
          </p>
        </div>,

        <div key="current" style={{ marginTop: 8 }}>
          <Typography variant="regular" component="h3" style={{ marginTop: 8 }}>
            Reguli curente
          </Typography>

          <p style={{ marginTop: '8px', color: '#666' }}>
            Active de la: {formatRoDate(effectiveFrom)}
          </p>

          <RulesBullets rule={currentRule} />

          <p style={{ marginTop: '8px', color: '#666', textAlign: 'center' }}>
            <i>
              Exemplu: pentru o valoare totală eligibilă de 1.500 RON, cashback-ul este de{' '}
              {currentRule.cashbackPercent}% × 1.500 ={' '}
              {(currentRule.cashbackPercent * 1500) / 100} RON (fără a depăși plafonul lunar).
            </i>
          </p>
        </div>,

        future.length > 0 ? (
          <div key="future" style={{ marginTop: 16 }}>
            <Typography variant="regular" component="h3" style={{ marginTop: 8 }}>
              Reguli viitoare
            </Typography>

            {future.map((r) => {
              const d = ruleToDate(r)
              return (
                <div key={r.id} style={{ marginTop: 12 }}>
                  <p style={{ margin: 0, color: '#666' }}>
                    Active de la: {formatRoDate(d)}
                  </p>
                  <RulesBullets rule={r} />
                </div>
              )
            })}
          </div>
        ) : null,

        history.length > 0 ? (
          <div key="history" style={{ marginTop: 16 }}>
            <Typography variant="regular" component="h3" style={{ marginTop: 8 }}>
              Istoric
            </Typography>

            {history.map((r) => {
              const d = ruleToDate(r)
              return (
                <div key={r.id} style={{ marginTop: 12 }}>
                  <p style={{ margin: 0, color: '#666' }}>
                    Active de la: {formatRoDate(d)}
                  </p>
                  <RulesBullets rule={r} />
                </div>
              )
            })}
          </div>
        ) : null
      ]}
      itemsPerRow={1}
    />
  )
}

export default EligibilityRulesPage
